/**
 * Sale Print Dialog - Diálogo pós-venda para impressão
 * P0-PRINT-01: Migrado para Template Builder via renderDocumentPdf
 * 
 * Fluxo:
 * 1. Tenta renderizar via template freeform do Template Builder
 * 2. Se não existir template → avisa utilizador e usa sistema antigo (fallback)
 * 3. PDF aberto autenticado via blob (sem window.open URL direto)
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Printer, FileText, Truck, Receipt, CheckCircle, 
  Download, Copy, Info, Loader2
} from 'lucide-react';
import { printAPI } from '@/lib/api';
import MissingMasterDataDialog from '@/components/MissingMasterDataDialog';
import { formatCurrency, formatDisplayOnlyCode } from '@/lib/utils';

export default function SalePrintDialog({ 
  open, 
  onOpenChange, 
  sale,
  onClose,
}) {
  const [printing, setPrinting] = useState(null); // 'pos' | 'invoice' | 'delivery'
  const [printed, setPrinted] = useState({});
  const [missingDataState, setMissingDataState] = useState({
    open: false,
    warnings: [],
    blob: null,
    filename: '',
    type: null,
  });
  const [printerTarget, setPrinterTarget] = useState('thermal');

  if (!sale) return null;

  /**
   * Handler genérico de impressão
   * @param {string} type - 'pos' | 'invoice' | 'delivery'
   * @param {string} doc_type - doc_type para o backend
   * @param {string} copyType - 'Original' | 'Duplicado'
   */
  const handlePrint = async (type, doc_type, copyType = 'Original') => {
    setPrinting(type);
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type,
        sale_id: sale.id,
        location_id: sale.location_id,
        module_group: 'VENDAS',
        copy_type: copyType,
      }, { deferOnMissingData: true });

      if (result.success) {
        if (result.hasMissingData) {
          setMissingDataState({
            open: true,
            warnings: result.warnings || [],
            blob: result.blob,
            filename: result.filename,
            type,
          });
          return;
        }

        setPrinted(prev => ({ ...prev, [type]: true }));
        if (result.usedFallback) {
          toast.warning('Template não configurado - usando modelo antigo', {
            description: result.warningMessage || 'Configure um template no Template Builder para personalizar este documento.',
            duration: 6000
          });
        } else {
          toast.success('Documento aberto para impressão', {
            description: 'Use Ctrl+P para imprimir ou guardar como PDF'
          });
        }
      } else {
        toast.error('Erro ao abrir documento', {
          description: result.error || 'Erro desconhecido'
        });
      }
    } catch (err) {
      toast.error('Erro inesperado ao imprimir');
    } finally {
      setPrinting(null);
    }
  };

  const handleMissingDataPrint = (reason) => {
    if (missingDataState.blob) {
      printAPI.openPdfBlob(missingDataState.blob, missingDataState.filename, 'preview');
      setPrinted(prev => ({ ...prev, [missingDataState.type]: true }));
      toast.warning('Impressão concluída com dados em falta', {
        description: reason ? `Motivo: ${reason}` : 'Considere completar o perfil do cliente.'
      });
    }
    setMissingDataState({ open: false, warnings: [], blob: null, filename: '', type: null });
  };

  const handleEditClient = () => {
    setMissingDataState({ open: false, warnings: [], blob: null, filename: '', type: null });
    const target = sale?.client_id ? `/clients?edit=${sale.client_id}` : '/clients';
    window.open(target, '_blank');
  };

  const handleClose = () => {
    setPrinted({});
    onOpenChange(false);
    if (onClose) onClose();
  };

  const isLoading = (type) => printing === type;
  const isPrinted = (type) => !!printed[type];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,720px)] max-w-[720px] max-h-[92vh] overflow-hidden p-4 sm:p-5" data-testid="sale-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Venda Concluída
          </DialogTitle>
          <DialogDescription>{formatDisplayOnlyCode(sale.sale_number, 'SAL')}</DialogDescription>
        </DialogHeader>
        
        {/* Sale Summary */}
        <div className="grid gap-2 rounded-xl border border-green-200 bg-green-50 p-3 sm:grid-cols-2" data-testid="sale-print-summary-card">
          <div className="rounded-lg bg-white/70 px-3 py-2" data-testid="sale-print-total-block">
            <p className="text-[11px] uppercase tracking-[0.14em] text-green-700">Total</p>
            <p className="text-lg font-bold text-green-700">{formatCurrency(sale.total_amount)}</p>
          </div>
          <div className="grid gap-1 text-sm" data-testid="sale-print-meta-block">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Cliente</span>
              <span className="truncate text-right font-medium">{sale.customer_name || 'Anónimo'}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Loja</span>
              <Badge variant="outline" data-testid="sale-print-location-badge">{sale.location_name}</Badge>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Items</span>
              <span className="font-medium" data-testid="sale-print-items-count">{sale.items?.length || 0} produto(s)</span>
            </div>
          </div>
        </div>
        
        <Separator />

        <div className="space-y-3" data-testid="sale-printer-selection-panel">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium text-sm">Escolha de impressora</h3>
              <p className="text-xs text-muted-foreground">Defina o contexto antes de abrir Original ou Duplicado.</p>
            </div>
            <Badge variant="outline" data-testid="sale-printer-selection-badge">
              Destino actual: {printerTarget === 'thermal' ? 'Térmica' : 'A4'}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={printerTarget === 'thermal' ? 'default' : 'outline'}
              onClick={() => setPrinterTarget('thermal')}
              data-testid="sale-printer-thermal-button"
            >
              Impressora Térmica
            </Button>
            <Button
              type="button"
              variant={printerTarget === 'a4' ? 'default' : 'outline'}
              onClick={() => setPrinterTarget('a4')}
              data-testid="sale-printer-a4-button"
            >
              Impressora A4
            </Button>
          </div>
        </div>
        
        <Separator />
        
        {/* Print Options */}
        <div className="space-y-3">
          <h3 className="font-medium text-sm">Opções de Impressão</h3>
          
          {/* POS Thermal */}
          <div className="rounded-xl border p-3 space-y-2" data-testid="sale-print-option-pos-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">Recibo POS (Térmica)</p>
                  <p className="text-xs text-muted-foreground">2 vias: Original + Duplicado com separação para corte automático</p>
                </div>
              </div>
              {isPrinted('pos') && (
                <Badge variant="secondary" className="text-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />Impresso
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handlePrint('pos', 'pos_receipt', 'Original')}
                disabled={printing !== null || printerTarget !== 'thermal'}
                className="flex-1"
                data-testid="print-pos-dual-btn"
              >
                {isLoading('pos') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
                {isLoading('pos') ? 'A abrir...' : 'Imprimir 2 vias'}
              </Button>
            </div>
            {printerTarget !== 'thermal' && (
              <p className="text-xs text-amber-700" data-testid="sale-print-pos-printer-note">Mude para impressora térmica para imprimir o recibo POS.</p>
            )}
          </div>
          
          {/* Invoice A4 */}
          <div className="rounded-xl border p-3 space-y-2" data-testid="sale-print-option-invoice-card">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-600" />
              <div>
                <p className="font-medium text-sm">Factura A4 (PDF)</p>
                <p className="text-xs text-muted-foreground">Documento fiscal completo</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePrint('invoice', 'invoice', 'Original')}
                disabled={printing !== null || printerTarget !== 'a4'}
                className="flex-1"
                data-testid="print-invoice-original-btn"
              >
                {isLoading('invoice') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Original
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePrint('invoice-dup', 'invoice', 'Duplicado')}
                disabled={printing !== null || printerTarget !== 'a4'}
                className="flex-1"
                data-testid="print-invoice-duplicate-btn"
              >
                {isLoading('invoice-dup') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}
                Duplicado
              </Button>
            </div>
            {printerTarget !== 'a4' && (
              <p className="text-xs text-amber-700" data-testid="sale-print-invoice-printer-note">A factura A4 deve seguir para impressora A4.</p>
            )}
          </div>
          
          {/* Delivery Note A4 */}
          <div className="rounded-xl border p-3 space-y-2" data-testid="sale-print-option-delivery-card">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-600" />
              <div>
                <p className="font-medium text-sm">Guia de Entrega A4 (PDF)</p>
                <p className="text-xs text-muted-foreground">Para entregas</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePrint('delivery', 'delivery_guide', 'Original')}
                disabled={printing !== null || printerTarget !== 'a4'}
                className="flex-1"
                data-testid="print-delivery-original-btn"
              >
                {isLoading('delivery') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Original
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePrint('delivery-dup', 'delivery_guide', 'Duplicado')}
                disabled={printing !== null || printerTarget !== 'a4'}
                className="flex-1"
                data-testid="print-delivery-duplicate-btn"
              >
                {isLoading('delivery-dup') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}
                Duplicado
              </Button>
            </div>
            {printerTarget !== 'a4' && (
              <p className="text-xs text-amber-700" data-testid="sale-print-delivery-printer-note">A guia A4 deve seguir para impressora A4.</p>
            )}
          </div>
        </div>
        
        <Separator />
        
        <Alert className="bg-blue-50 border-blue-200" data-testid="sale-print-help-alert">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-xs text-blue-700">
            <strong>Como imprimir:</strong> Os documentos abrem em nova janela.
            Use <kbd className="px-1 bg-blue-100 rounded">Ctrl+P</kbd> para imprimir.
            No POS, o PDF sai em duas vias separadas para facilitar o recorte automático.
            Se aparecer aviso "Template não configurado", configure um template no Template Builder.
          </AlertDescription>
        </Alert>

        <MissingMasterDataDialog
          open={missingDataState.open}
          onOpenChange={(value) =>
            setMissingDataState(prev =>
              value ? { ...prev, open: true } : { open: false, warnings: [], blob: null, filename: '', type: null }
            )
          }
          warnings={missingDataState.warnings}
          customerName={sale.customer_name || 'Cliente'}
          onEditClient={handleEditClient}
          onPrintAnyway={handleMissingDataPrint}
        />
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="close-print-dialog-btn">
            Fechar
          </Button>
          <Button onClick={handleClose} data-testid="complete-print-dialog-btn">
            <CheckCircle className="h-4 w-4 mr-1" />
            Concluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
